import { useEffect, useState } from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  FormControlLabel,
  Slide,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { APIEndpoint } from 'common/enum';
import { addressFormInterface } from 'common/interface';
import {
  universalGetRequest,
  universalPutRequest,
} from 'common/requestHandler';
import useNavigateWithUUID from 'hooks/useNavigateWithUUID';
import { popupNotification } from 'notifications/ToastNotification';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as yup from 'yup';

import AddressUpdateComp from '../../components/address/AddressUpdateComp';
import OverlayLoader from '../../components/elements/OverlayLoader';
import FileUploadModal from '../../components/fileComponent/FileUpload';
import { useFileUpload } from '../../hooks/useFileUpload';
import colors from '../../theme/colors';

// Beautiful styled components
const BeautifulCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: '#ffffff',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  position: 'relative',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  paddingBottom: theme.spacing(2),
}));

const SectionIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${colors.gradientColor1} 0%, ${colors.gradientColor2} 100%)`,
  color: 'white',
  boxShadow: `0 4px 20px rgba(66, 211, 146, 0.3)`,
  transition: 'all 0.3s ease',

  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    boxShadow: `0 8px 30px rgba(66, 211, 146, 0.4)`,
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 55,
  height: 55,
  background: `linear-gradient(135deg, ${colors.gradientColor1} 0%, ${colors.gradientColor2} 100%)`,
  fontSize: '1rem',
  fontWeight: 500,
  boxShadow: `0 8px 25px rgba(66, 211, 146, 0.3)`,
  border: '4px solid white',
  transition: 'all 0.3s ease',

  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `0 12px 35px rgba(66, 211, 146, 0.4)`,
  },
}));

const BeautifulButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.gradientColor1} 0%, ${colors.gradientColor2} 100%)`,
  color: 'white',
  border: 'none',
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: `0 4px 20px rgba(66, 211, 146, 0.3)`,
  transition: 'all 0.3s ease',

  '&:hover': {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.gradientColor2} 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 30px rgba(66, 211, 146, 0.4)`,
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  color: colors.primary,
  border: `2px solid rgba(18, 112, 220, 0.2)`,
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 20px',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(18, 112, 220, 0.1)',
    borderColor: colors.primary,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 15px rgba(18, 112, 220, 0.2)`,
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(135deg, rgba(18, 112, 220, 0.1) 0%, rgba(100, 126, 255, 0.1) 100%)`,
  color: colors.primary,
  fontWeight: 600,
  border: `1px solid rgba(18, 112, 220, 0.2)`,
  borderRadius: 8,
  '& .MuiChip-label': {
    padding: '6px 12px',
  },
}));

const BeautifulSwitch = styled(Switch)(({ theme }) => ({
  width: 40,
  height: 22,
  padding: 0,

  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(1px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(17px)',
      '& + .MuiSwitch-track': {
        backgroundColor: colors.primary,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
  },

  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 18,
    height: 18,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },

  '& .MuiSwitch-track': {
    borderRadius: 11,
    backgroundColor: '#ccc',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

// Beautiful styled TextField matching InputElement styling
const BeautifulInputField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: `1px solid rgba(18, 112, 220, 0.15)`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    paddingTop: '8px',

    '&:hover': {
      borderColor: colors.primary,
      boxShadow: `0 2px 8px rgba(18, 112, 220, 0.12)`,
    },

    '&.Mui-focused': {
      borderColor: colors.primary,
      boxShadow: `0 4px 12px rgba(18, 112, 220, 0.2)`,
    },

    '&.Mui-error': {
      borderColor: colors.errorMain,
      boxShadow: `0 2px 8px rgba(255, 99, 71, 0.15)`,

      '&:hover': {
        borderColor: colors.errorMain,
        boxShadow: `0 4px 12px rgba(255, 99, 71, 0.2)`,
      },
    },

    '& .MuiOutlinedInput-input': {
      padding: '8px 14px',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#2d3748',
      transition: 'all 0.3s ease',

      '&::placeholder': {
        color: '#a0aec0',
        opacity: 1,
        fontWeight: 400,
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },

  '& .MuiInputLabel-root': {
    color: '#718096',
    fontWeight: 500,
    fontSize: '0.85rem',
    transition: 'all 0.3s ease',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: '0 4px',

    '&.Mui-focused': {
      color: colors.primary,
      fontWeight: 600,
    },

    '&.Mui-error': {
      color: colors.errorMain,
    },

    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
  },

  '& .MuiFormHelperText-root': {
    margin: '4px 0 0 4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    transition: 'all 0.3s ease',

    '&.Mui-error': {
      color: colors.errorMain,
      animation: 'shake 0.5s ease-in-out',
    },
  },

  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '25%': { transform: 'translateX(-2px)' },
    '75%': { transform: 'translateX(2px)' },
  },
}));

const validationSchema = yup.object().shape({
  groupEmailAddress: yup.string().email('Enter a valid email'),
});

const PreferenceAdmin = () => {
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [groupEmailAddress, setGroupEmailAddress] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const { companyId, username, firstName, lastName } = useSelector(
    (state: RootState) => state.auth
  );
  const [userDefaultAddress, setUserDefaultAddress] = useState(
    {} as addressFormInterface
  );
  const [companyDefaultAddress, setCompanyDefaultAddress] = useState({} as any);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [emailError, setEmailError] = useState('');
  const {
    uploadFiles,
    progress,
    isUploading,
    setSelectedFiles,
    clearFiles,
    files,
  } = useFileUpload();
  const { navigateWithUuid } = useNavigateWithUUID();
  const [userEmail, setUserEmail] = useState('');

  // ! This updates entity details
  const updateEntityDetails = async (companyAddress?: addressFormInterface) => {
    try {
      await validationSchema.validate({ groupEmailAddress });
      const updatedCompanyAddress = companyAddress ?? companyDefaultAddress;
      const data = {
        admin_user_email: adminEmail,
        ...updatedCompanyAddress,
        group_email: groupEmailAddress,
        notify_when_new_files_added_by_customers: notificationEnabled,
      };
      const payload = {
        companyId: companyId,
        entityDetails: JSON.stringify(data),
        actionName: 'company',
      };
      const response = await universalPutRequest(
        APIEndpoint.updateUserDetails,
        payload
      );
      if ('error' in response) {
        popupNotification(response.error, false);
      } else {
        popupNotification(response.message, true);
      }
    } catch (error: any) {
      setEmailError(error.message);
    }
  };

  const getCompanyDetails = async () => {
    setLoading(true);
    const response: any = await universalGetRequest(
      APIEndpoint.getUserDetails,
      {
        actionName: 'company',
        username: username,
        companyId,
      }
    );
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      setCompanyDefaultAddress(response.data.companyDetails);
      setNotificationEnabled(
        response.data.companyDetails.notify_when_new_files_added_by_customers
      );
      setAdminEmail(response.data.companyDetails?.admin_user_email);
      setGroupEmailAddress(response.data.companyDetails?.group_email);
      setUserDefaultAddress(JSON.parse(response.data.address));
      setUserEmail(response?.data.email);
    }
    setLoading(false);
  };

  // !Upload company logo
  const handleUpload = async () => {
    await uploadFiles(
      companyId,
      'media',
      firstName,
      lastName,
      '',
      setShowModal,
      null
    );
    clearFiles(); // Clear files after upload
  };

  useEffect(() => {
    getCompanyDetails();
  }, []);

  const abc = (data: addressFormInterface) => {
    updateEntityDetails(data);
  };

  if (loading) {
    return <OverlayLoader loading={loading} text="Please wait..." />;
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: 4,
        minHeight: '100vh',
      }}
    >
      {/* Profile Information Section */}
      <Fade in timeout={800}>
        <BeautifulCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <SectionHeader>
              <SectionIcon>
                <PersonIcon />
              </SectionIcon>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#2d3748"
                  gutterBottom
                >
                  Profile Information
                </Typography>
                <Typography variant="body1" color="#718096">
                  Manage your account details and preferences
                </Typography>
              </Box>
            </SectionHeader>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <UserAvatar>
                {firstName?.charAt(0)?.toUpperCase()}
                {lastName?.charAt(0)?.toUpperCase()}
              </UserAvatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  color="#2d3748"
                  gutterBottom
                >
                  {firstName} {lastName}
                </Typography>
                {/* <Typography variant="body2" color="#718096" mb={2}>
                  Administrator Account
                </Typography> */}
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                      variant="body2"
                      color="#718096"
                      fontWeight={500}
                    >
                      Username:
                    </Typography>
                    <StatusChip label={username} size="small" />
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                      variant="body2"
                      color="#718096"
                      fontWeight={500}
                    >
                      Email:
                    </Typography>
                    <StatusChip label={userEmail} size="small" />
                  </Box>
                </Box>
              </Box>
              <SecondaryButton
                onClick={() => navigateWithUuid('change-password')}
                startIcon={<LockIcon />}
              >
                Change Password
              </SecondaryButton>
            </Box>
          </CardContent>
        </BeautifulCard>
      </Fade>

      {/* Company Settings Section */}
      <Slide direction="up" in timeout={1000}>
        <BeautifulCard sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <SectionHeader>
              <SectionIcon>
                <SettingsIcon />
              </SectionIcon>
              <Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="#2d3748"
                  gutterBottom
                >
                  Company Settings
                </Typography>
                <Typography variant="body1" color="#718096">
                  Configure company preferences and branding
                </Typography>
              </Box>
            </SectionHeader>

            {/* Logo Upload Section */}
            <Box sx={{ mb: 5 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#2d3748"
                    gutterBottom
                  >
                    Company Logo
                  </Typography>
                  <Typography variant="body2" color="#718096">
                    Upload your company logo to personalize your documents
                  </Typography>
                </Box>
                <SecondaryButton
                  onClick={() => setShowModal(true)}
                  startIcon={<UploadIcon />}
                >
                  Upload Logo
                </SecondaryButton>
              </Box>
            </Box>

            <Divider sx={{ my: 4, borderColor: 'rgba(18, 112, 220, 0.1)' }} />

            {/* Notification Settings */}
            <Box sx={{ mb: 5 }}>
              <Box display="flex" alignItems="center" gap={2} mb={4}>
                <NotificationsIcon
                  sx={{ color: colors.primary, fontSize: 28 }}
                />
                <Typography variant="h6" fontWeight={600} color="#2d3748">
                  Notification Preferences
                </Typography>
              </Box>

              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <BeautifulSwitch
                      checked={notificationEnabled}
                      onChange={(e) => setNotificationEnabled(e.target.checked)}
                    />
                  }
                  label={
                    <Box ml={2}>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color="#2d3748"
                      >
                        Customer file notifications
                      </Typography>
                      <Typography variant="body2" color="#718096">
                        Get notified when customers upload new files
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', margin: 0 }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="#2d3748"
                  gutterBottom
                >
                  Group email for notifications
                </Typography>
                <BeautifulInputField
                  fullWidth
                  size="medium"
                  label="Group email address"
                  placeholder="Enter group email address"
                  value={groupEmailAddress}
                  error={!!emailError}
                  helperText={emailError || ''}
                  onChange={(e) => setGroupEmailAddress(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box display="flex" alignItems="center" gap={1}>
                  <InfoIcon fontSize="small" sx={{ color: colors.primary }} />
                  <Typography variant="body2" color="#718096">
                    Admin will receive notifications if group email is not
                    provided
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* <Divider sx={{ my: 4, borderColor: 'rgba(18, 112, 220, 0.1)' }} /> */}

            {/* Update Button */}
            <Box display="flex" justifyContent="flex-end">
              <SecondaryButton onClick={() => updateEntityDetails()}>
                Save Changes
              </SecondaryButton>
            </Box>
          </CardContent>
        </BeautifulCard>
      </Slide>

      {/* Address Sections */}
      <Slide direction="up" in timeout={1200}>
        <Box sx={{ mb: 4 }}>
          <SectionHeader>
            <SectionIcon>
              <BusinessIcon />
            </SectionIcon>
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color="#2d3748"
                gutterBottom
              >
                Address Management
              </Typography>
              <Typography variant="body1" color="#718096">
                Manage company and personal address information
              </Typography>
            </Box>
          </SectionHeader>

          <BeautifulCard sx={{ mb: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                color="#2d3748"
                gutterBottom
              >
                Company Address
              </Typography>
              <AddressUpdateComp
                handleSubmitForm={abc}
                addressDefaultValues={companyDefaultAddress}
              />
            </CardContent>
          </BeautifulCard>

          <BeautifulCard>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                color="#2d3748"
                gutterBottom
              >
                Your Address
              </Typography>
              <AddressUpdateComp addressDefaultValues={userDefaultAddress} />
            </CardContent>
          </BeautifulCard>
        </Box>
      </Slide>

      {/* File Upload Modal */}
      <FileUploadModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          clearFiles(); // Clear files when modal closes
        }}
        onFilesSelected={setSelectedFiles}
        progress={progress}
        onUpload={handleUpload}
        uploading={isUploading}
        files={files}
      />
    </Box>
  );
};

export default PreferenceAdmin;
